import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, RefreshCw, Send, AlertTriangle, X, CheckCircle, Globe } from 'lucide-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import MainService from '../../services/MainService';
import SmsKeywordTextField from './SmsKeywordTextField';
import SearchableMultiSelect from '../ui/SearchableMultiSelect';

const DraftSMS = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const [draft, setDraft] = useState({
    Message: '',
    SelectedClientIds: [],
    SelectedCustomerIds: [],
    IsAllClients: false,
    IsAllCustomers: false,
    IsGeography: false,
    SelectedAreasIds: [],
    IsAreaClient: true,
    IsAreaCustomer: true
  });

  const [clients, setClients] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const init = () => {
    setDraft({
      Message: '',
      SelectedClientIds: [],
      SelectedCustomerIds: [],
      IsAllClients: false,
      IsAllCustomers: false,
      IsGeography: false,
      SelectedAreasIds: [],
      IsAreaClient: true,
      IsAreaCustomer: true
    });
    setClients([]);
    setCustomers([]);
    setAreas([]);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDraft(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear selections when "All" is checked
    if (name === 'IsAllClients' && checked) {
      setDraft(prev => ({ ...prev, SelectedClientIds: [] }));
    }
    if (name === 'IsAllCustomers' && checked) {
      setDraft(prev => ({ ...prev, SelectedCustomerIds: [] }));
    }
  };

  const searchClients = async (keyword) => {
    try {
      const response = await MainService.ClientsInAutoComplete({
        filter: {
          filters: [{
            field: 'Name',
            operator: 'contains',
            value: keyword
          }],
          logic: 'and'
        }
      });
      
      if (response && response.Data) {
        setClients(response.Data);
      } else if (Array.isArray(response)) {
        setClients(response);
      } else {
        setClients([]);
      }
    } catch (err) {
      console.error('Error searching clients:', err);
      setClients([]);
    }
  };

  const searchCustomers = async (keyword) => {
    try {
      const response = await MainService.CustomersInAutoComplete({
        filter: {
          filters: [{
            field: 'Name',
            operator: 'contains',
            value: keyword
          }],
          logic: 'and'
        }
      });
      
      if (response && response.Data) {
        setCustomers(response.Data);
      } else if (Array.isArray(response)) {
        setCustomers(response);
      } else {
        setCustomers([]);
      }
    } catch (err) {
      console.error('Error searching customers:', err);
      setCustomers([]);
    }
  };

  const searchAreas = async (keyword) => {
    try {
      const response = await MainService.AreasInGrid({
        filter: {
          filters: [{
            field: 'Name',
            operator: 'contains',
            value: keyword
          }],
          logic: 'and'
        }
      });
      
      if (response && response.Data) {
        setAreas(response.Data);
      } else if (Array.isArray(response)) {
        setAreas(response);
      } else {
        setAreas([]);
      }
    } catch (err) {
      console.error('Error searching areas:', err);
      setAreas([]);
    }
  };

  const handleSendSMS = () => {
    if (!draft.Message.trim()) {
      setError('Enter SMS content');
      return;
    }

    if (!draft.IsAllClients && !draft.IsAllCustomers && 
        draft.SelectedClientIds.length === 0 && draft.SelectedCustomerIds.length === 0 &&
        !draft.IsGeography) {
      setError('Select Customer or Clients');
      return;
    }

    if (draft.IsGeography && draft.SelectedAreasIds.length === 0) {
      setError('Select Areas');
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSMS = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await MainService.DraftSMS(draft);
      
      if (response === 'Success' || response === true) {
        setSuccess('SMS sent successfully');
        setShowConfirmModal(false);
        init();
      } else {
        setError(response || 'Something went wrong');
      }
    } catch (err) {
      console.error('Error sending SMS:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-6 pb-4 border-b border-gray-200">
          <MessageSquare className="w-6 h-6 text-[#46955A] mr-2" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Draft SMS</h1>
            <p className="text-sm text-gray-500 mt-1">Compose and send SMS to clients and customers</p>
          </div>
        </div>

        {/* Message Textarea */}
        <div className="mb-6">
          <SmsKeywordTextField
            fullWidth
            multiline
            rows={10}
            placeholder="Draft your message here..."
            value={draft.Message}
            onValueChange={(value) => setDraft(prev => ({ ...prev, Message: value }))}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f5f5f5',
                '&:hover fieldset': {
                  borderColor: '#46955A',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#46955A',
                },
              },
            }}
          />
        </div>

        {/* Clients Selection */}
        <div className="mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-11">
              <SearchableMultiSelect
                value={draft.SelectedClientIds}
                onChange={(ids) => setDraft(prev => ({ ...prev, SelectedClientIds: ids }))}
                options={clients}
                placeholder="Select Clients..."
                disabled={draft.IsAllClients}
                label="Clients"
                onSearch={searchClients}
              />
            </div>
            <div className="lg:col-span-1 flex items-end pb-2">
              <FormControlLabel
                control={
                  <Checkbox
                    name="IsAllClients"
                    checked={draft.IsAllClients}
                    onChange={handleInputChange}
                    sx={{
                      color: '#46955A',
                      '&.Mui-checked': {
                        color: '#46955A',
                      },
                    }}
                  />
                }
                label="All"
                sx={{ margin: 0 }}
              />
            </div>
          </div>
        </div>

        {/* Customers Selection */}
        <div className="mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-11">
              <SearchableMultiSelect
                value={draft.SelectedCustomerIds}
                onChange={(ids) => setDraft(prev => ({ ...prev, SelectedCustomerIds: ids }))}
                options={customers}
                placeholder="Select Customers..."
                disabled={draft.IsAllCustomers}
                label="Customers"
                onSearch={searchCustomers}
              />
            </div>
            <div className="lg:col-span-1 flex items-end pb-2">
              <FormControlLabel
                control={
                  <Checkbox
                    name="IsAllCustomers"
                    checked={draft.IsAllCustomers}
                    onChange={handleInputChange}
                    sx={{
                      color: '#46955A',
                      '&.Mui-checked': {
                        color: '#46955A',
                      },
                    }}
                  />
                }
                label="All"
                sx={{ margin: 0 }}
              />
            </div>
          </div>
        </div>

        {/* By Geography Section */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Globe className="w-5 h-5 text-[#46955A]" />
            <h3 className="text-lg font-semibold text-gray-900">By Geography</h3>
            <FormControlLabel
              control={
                <Checkbox
                  name="IsGeography"
                  checked={draft.IsGeography}
                  onChange={handleInputChange}
                  sx={{
                    color: '#46955A',
                    '&.Mui-checked': {
                      color: '#46955A',
                    },
                  }}
                />
              }
              label=""
              sx={{ margin: 0 }}
            />
          </div>
          <p className="text-sm text-gray-600 mb-4">Send SMS by areas</p>

          {draft.IsGeography && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex gap-6 mb-4">
                <FormControlLabel
                  control={
                    <Checkbox
                      name="IsAreaClient"
                      checked={draft.IsAreaClient}
                      onChange={handleInputChange}
                      sx={{
                        color: '#46955A',
                        '&.Mui-checked': {
                          color: '#46955A',
                        },
                      }}
                    />
                  }
                  label="Client"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="IsAreaCustomer"
                      checked={draft.IsAreaCustomer}
                      onChange={handleInputChange}
                      sx={{
                        color: '#46955A',
                        '&.Mui-checked': {
                          color: '#46955A',
                        },
                      }}
                    />
                  }
                  label="Customer"
                />
              </div>
              <div>
                <SearchableMultiSelect
                  value={draft.SelectedAreasIds}
                  onChange={(ids) => setDraft(prev => ({ ...prev, SelectedAreasIds: ids }))}
                  options={areas}
                  placeholder="Select Areas..."
                  label="Areas"
                  onSearch={searchAreas}
                />
              </div>
            </div>
          )}
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="text-center py-4">
            <CircularProgress size={32} sx={{ color: '#46955A' }} />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button
            variant="outlined"
            startIcon={<RefreshCw className="w-4 h-4" />}
            onClick={init}
            disabled={loading}
            sx={{
              borderColor: '#46955A',
              color: '#46955A',
              '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' },
              '&:disabled': { borderColor: '#9CA3AF', color: '#9CA3AF' }
            }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Send className="w-4 h-4" />}
            onClick={handleSendSMS}
            disabled={loading}
            sx={{
              backgroundColor: '#46955A',
              '&:hover': { backgroundColor: '#3d7d4f' },
              '&:disabled': { backgroundColor: '#9CA3AF' }
            }}
          >
            Send SMS
          </Button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
      >
        <DialogTitle sx={{ color: '#111827', fontWeight: 600 }}>
          Confirm Send SMS
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#374151' }}>
            Are you sure you want to send this SMS?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowConfirmModal(false)}
            sx={{ color: '#6B7280' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSMS}
            disabled={loading}
            variant="contained"
            sx={{
              backgroundColor: '#46955A',
              '&:hover': { backgroundColor: '#3d7d4f' },
              '&:disabled': { backgroundColor: '#9CA3AF' }
            }}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Send className="w-4 h-4" />}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DraftSMS;

